import React, { useEffect, useRef, useState } from 'react';
import { useOS } from './OSContext';
import apps, { getApp } from './apps';

function useClock() {
	const [now, setNow] = useState(new Date());
	useEffect(() => {
		const id = setInterval(() => setNow(new Date()), 1000);
		return () => clearInterval(id);
	}, []);
	return now;
}

export default function MenuBar({ onOpenSpotlight }) {
	const { activeId, windows, setTheme, openApp } = useOS();
	const now = useClock();
	const [openMenu, setOpenMenu] = useState(null);
	const barRef = useRef(null);

	useEffect(() => {
		function handleClickOutside(e) {
			if (barRef.current && !barRef.current.contains(e.target)) {
				setOpenMenu(null);
			}
		}
		function handleEscape(e) {
			if (e.key === 'Escape') setOpenMenu(null);
		}
		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscape);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscape);
		};
	}, []);

	const activeTitle = (activeId && windows[activeId]?.appId && apps.find(a => a.id === activeId)?.title) || 'Finder';

	const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	const dateStr = now.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });

	function toggleMenu(name) {
		setOpenMenu(prev => (prev === name ? null : name));
	}

	function handleAbout() {
		openApp(getApp('about'));
		setOpenMenu(null);
	}

	function handleGameMode() {
		setTheme('v2');
		setOpenMenu(null);
	}

	return (
		<div
			ref={barRef}
			className="fixed top-0 left-0 right-0 z-50 h-7 flex items-center justify-between px-2 bg-os-chrome/90 backdrop-blur-xl border-b border-white/10 text-os-text text-[13px] font-os select-none"
		>
			<div className="flex items-center h-full">
				<div className="relative h-full flex items-center">
					<button
						onClick={() => toggleMenu('logo')}
						className="h-full px-2.5 flex items-center justify-center rounded-sm hover:bg-white/10 active:bg-white/15 transition-colors"
						aria-label="System menu"
					>
						<i className="fa-solid fa-code text-os-accent text-[13px]" />
					</button>
					{openMenu === 'logo' && (
						<div className="absolute top-7 left-0 min-w-[220px] rounded-lg bg-os-surface2/95 backdrop-blur-xl border border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.5)] py-1 overflow-hidden">
							<button
								onClick={handleAbout}
								className="w-full text-left px-3 py-1.5 text-[13px] text-os-text hover:bg-os-accent hover:text-white transition-colors"
							>
								About This Portfolio
							</button>
						</div>
					)}
				</div>

				<span className="font-semibold px-1.5 whitespace-nowrap">{activeTitle}</span>

				<button
					onClick={handleGameMode}
					className="flex items-center gap-1.5 h-full px-2.5 rounded-sm hover:bg-white/10 active:bg-white/15 transition-colors text-os-textdim hover:text-os-text whitespace-nowrap"
				>
					<i className="fa-solid fa-gamepad" />
					Game Mode
				</button>

				<div className="hidden sm:flex items-center h-full">
					{['View'].map(label => (
						<button
							key={label}
							onClick={() => toggleMenu(label)}
							className="h-full px-2.5 rounded-sm hover:bg-white/10 active:bg-white/15 transition-colors text-os-textdim hover:text-os-text"
						>
							{label}
						</button>
					))}

					<div className="relative h-full flex items-center">
						<button
							onClick={() => toggleMenu('Help')}
							className="h-full px-2.5 rounded-sm hover:bg-white/10 active:bg-white/15 transition-colors text-os-textdim hover:text-os-text"
						>
							Help
						</button>
						{openMenu === 'Help' && (
							<div className="absolute top-7 left-0 min-w-[220px] rounded-lg bg-os-surface2/95 backdrop-blur-xl border border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.5)] py-1 overflow-hidden">
								<button
									onClick={handleAbout}
									className="w-full text-left px-3 py-1.5 text-[13px] text-os-text hover:bg-os-accent hover:text-white transition-colors"
								>
									About This Portfolio
								</button>
							</div>
						)}
					</div>
				</div>
			</div>

			<div className="flex items-center gap-3 h-full pr-1">
				<div className="hidden sm:flex items-center gap-3">
					<i className="fa-solid fa-wifi text-[12px] text-os-text/90" />
					<i className="fa-solid fa-volume-high text-[12px] text-os-text/90" />
					<i className="fa-solid fa-battery-three-quarters text-[13px] text-os-text/90" />
				</div>
				<button
					onClick={onOpenSpotlight}
					className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/10 active:bg-white/15 transition-colors"
					aria-label="Search"
				>
					<i className="fa-solid fa-magnifying-glass text-[12px] text-os-text/90" />
				</button>
				<div className="flex items-baseline gap-1.5 tabular-nums whitespace-nowrap">
					<span className="hidden sm:inline text-os-textdim text-[12px]">{dateStr}</span>
					<span className="font-medium">{timeStr}</span>
				</div>
			</div>
		</div>
	);
}
